import { Avatar, Box, Center, Container, Grid, GridItem, HStack, Image, Tooltip, VStack, useDisclosure } from '@chakra-ui/react';
import { keyframes } from '@chakra-ui/system';
import { useCreateRoastMutation } from 'hooks/mutations/roast/useCreateRoastMutation';
import { useDeleteRoastMutation } from 'hooks/mutations/roast/useDeleteRoastMutation';
import { useGetRoastCreatorQuery } from 'hooks/queries/roast/useGetRoastCreatorQuery';
import { useGetRoastDetailQuery } from 'hooks/queries/roast/useGetRoastDetailQuery';
import { useGetRoastUpvotersQuery } from 'hooks/queries/roast/useGetRoastUpvotersQuery';
import { useScrollToTop } from 'hooks/useScrollToTop';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import Button from 'components/Button';
import { LinkInput } from 'components/LinkInput';
import Modal from 'components/Modal';
import PageLoader from 'components/PageLoader';
import { SEO } from 'components/SEO';
import ShareButtons from 'components/ShareButtons';
import Text from 'components/Text';
import UserAvatar from 'components/UserAvatar';
import { ERoute } from 'types/frontend';

import RoastCommentSection from './RoastCommentSection';
import RoastMetadataEditor from './RoastMetadataEditor';
import RoastVoteButton from './RoastVoteButton';
import {
  CATEGORY_DISPLAY_LABELS,
  CATEGORY_LABELS,
  getCategoryScoreColor,
  getOverallScoreColor,
  MAX_SCORE_PER_CATEGORY,
  PRICING_DISPLAY_LABELS,
  ROAST_CATEGORIES,
  ROAST_PUBLIC_BASE,
  type CategoryScore,
} from '../utils';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const getCreatorDisplayName = (name: string | null, screenName: string): string => {
  if (name && !UUID_RE.test(name)) return name;
  if (screenName && !UUID_RE.test(screenName)) return screenName;
  return 'Anonymous';
};

// --- Category config with icons ---
const CATEGORY_ICONS: Record<string, string> = {
  the_vibe: '✨',
  design_ui: '🎨',
  copy_messaging: '✍️',
  trust_factor: '🛡️',
  idea_value_prop: '💡',
  ux_flow: '🧭',
  mobile_performance: '📱',
  x_factor: '⚡',
};

// --- Score glow/border helpers (Chakra CSS-in-JS equivalents) ---
const getOverallBorderColor = (score: number): string => {
  if (score <= 25) return 'red.500';
  if (score <= 50) return 'orange.500';
  if (score <= 70) return 'yellow.500';
  if (score <= 85) return 'green.400';
  return 'green.300';
};

const getOverallGlowShadow = (score: number): string => {
  if (score <= 25) return '0 4px 20px rgba(239,68,68,0.15)';
  if (score <= 50) return '0 4px 20px rgba(249,115,22,0.15)';
  if (score <= 70) return '0 4px 20px rgba(234,179,8,0.15)';
  if (score <= 85) return '0 4px 20px rgba(74,222,128,0.15)';
  return '0 4px 20px rgba(52,211,153,0.15)';
};

// --- Roast text section parsing (same as landing page) ---
interface RoastSection {
  title: string;
  content: string;
  type: 'verdict' | 'good' | 'brutal' | 'other';
}

const SECTION_STYLES: Record<RoastSection['type'], { border: string; borderBottom: string; bgGradient: string; titleColor: string; accentBg: string }> = {
  verdict: { border: 'rgba(249,115,22,0.2)', borderBottom: 'rgba(249,115,22,0.2)', bgGradient: 'linear(to-r, rgba(249,115,22,0.04), transparent)', titleColor: 'orange.400', accentBg: 'rgba(249,115,22,0.15)' },
  good: { border: 'rgba(52,211,153,0.2)', borderBottom: 'rgba(52,211,153,0.2)', bgGradient: 'linear(to-r, rgba(52,211,153,0.04), transparent)', titleColor: 'green.400', accentBg: 'rgba(52,211,153,0.15)' },
  brutal: { border: 'rgba(248,113,113,0.2)', borderBottom: 'rgba(248,113,113,0.2)', bgGradient: 'linear(to-r, rgba(248,113,113,0.04), transparent)', titleColor: 'red.400', accentBg: 'rgba(248,113,113,0.15)' },
  other: { border: 'rgba(255,255,255,0.1)', borderBottom: 'rgba(255,255,255,0.1)', bgGradient: 'linear(to-r, rgba(255,255,255,0.02), transparent)', titleColor: 'whiteAlpha.700', accentBg: 'rgba(255,255,255,0.05)' },
};

const SECTION_ICONS: Record<RoastSection['type'], string> = {
  verdict: 'ⓘ',
  good: '👍',
  brutal: '🔥',
  other: '💬',
};

const parseRoastSections = (text: string): RoastSection[] => {
  if (!text) return [];
  const parts = text.split(/^## /m);
  const sections: RoastSection[] = [];

  for (let i = 1; i < parts.length; i++) {
    const nlIdx = parts[i].indexOf('\n');
    if (nlIdx === -1) continue;
    const title = parts[i].substring(0, nlIdx).trim();
    const content = parts[i].substring(nlIdx + 1).trim();
    if (!content) continue;

    let type: RoastSection['type'] = 'other';
    const lower = title.toLowerCase();
    if (lower.includes('verdict')) type = 'verdict';
    else if (lower.includes('good') || lower.includes('praise') || lower.includes('strengths')) type = 'good';
    else if (lower.includes('brutal') || lower.includes('truth') || lower.includes('bad') || lower.includes('ugly')) type = 'brutal';

    sections.push({ title, content, type });
  }

  if (sections.length === 0 && text.trim()) {
    sections.push({ title: 'The Roast', content: text, type: 'verdict' });
  }

  return sections;
};

// Simple markdown-to-JSX renderer for roast text
const RoastProse = ({ content }: { content: string }) => {
  const lines = content.split('\n');

  return (
    <VStack spacing={2} alignItems="flex-start" width="100%">
      {lines.map((line, idx) => {
        const trimmed = line.trim();
        if (!trimmed) return null;

        // Bold text
        const rendered = trimmed.replace(/\*\*(.+?)\*\*/g, '⟨b⟩$1⟨/b⟩');
        const parts = rendered.split(/⟨\/?b⟩/);

        const isBullet = trimmed.startsWith('- ') || trimmed.startsWith('* ');
        const displayText = isBullet ? trimmed.slice(2) : trimmed;
        const displayRendered = isBullet
          ? displayText.replace(/\*\*(.+?)\*\*/g, '⟨b⟩$1⟨/b⟩')
          : rendered;
        const displayParts = displayRendered.split(/⟨\/?b⟩/);

        return (
          <HStack key={idx} spacing={2} alignItems="flex-start" width="100%">
            {isBullet && (
              <Box as="span" color="orange.400" mt="2px" flexShrink={0}>•</Box>
            )}
            <Text fontSize="small" opacity="primary" lineHeight="1.85">
              {displayParts.map((part, pIdx) =>
                pIdx % 2 === 1 ? (
                  <Box key={pIdx} as="span" fontWeight="bold" color="white">
                    {part}
                  </Box>
                ) : (
                  <Box key={pIdx} as="span">{part}</Box>
                )
              )}
            </Text>
          </HStack>
        );
      })}
    </VStack>
  );
};

// --- Score Bar (compact, for sidebar) ---
const ScoreBar = ({ label, icon, score }: { label: string; icon: string; score: number }) => {
  const pct = (score / MAX_SCORE_PER_CATEGORY) * 100;

  return (
    <Box width="100%">
      <HStack justifyContent="space-between" mb={0.5}>
        <HStack spacing={1.5}>
          <Box as="span" style={{ fontSize: '11px' }}>{icon}</Box>
          <Text fontSize="xMicro" opacity="secondary" fontWeight="medium">{label}</Text>
        </HStack>
        <Text fontSize="xMicro" fontWeight="bold" color={getCategoryScoreColor(score)}>
          {score}/{MAX_SCORE_PER_CATEGORY}
        </Text>
      </HStack>
      <Box width="100%" height="6px" backgroundColor="whiteAlpha.50" borderRadius="full" overflow="hidden">
        <Box
          height="100%"
          borderRadius="full"
          backgroundColor={getCategoryScoreColor(score)}
          width={`${pct}%`}
          transition="width 0.7s ease-out"
        />
      </Box>
    </Box>
  );
};

// --- Score Bar with roast text (for breakdown tab) ---
const ScoreBarFull = ({ label, icon, score, roastText }: { label: string; icon: string; score: number; roastText: string }) => {
  const pct = (score / MAX_SCORE_PER_CATEGORY) * 100;

  return (
    <Box
      p={4}
      borderRadius="lg"
      border="1px solid"
      borderColor="whiteAlpha.100"
      backgroundColor="whiteAlpha.50"
    >
      <HStack justifyContent="space-between" mb={2}>
        <HStack spacing={2}>
          <Box as="span" style={{ fontSize: '14px' }}>{icon}</Box>
          <Text fontSize="medium" fontWeight="semibold">{label}</Text>
        </HStack>
        <Text fontSize="medium" fontWeight="bold" color={getCategoryScoreColor(score)}>
          {score}/{MAX_SCORE_PER_CATEGORY}
        </Text>
      </HStack>
      <Box width="100%" height="8px" backgroundColor="whiteAlpha.50" borderRadius="full" overflow="hidden" mb={3}>
        <Box
          height="100%"
          borderRadius="full"
          backgroundColor={getCategoryScoreColor(score)}
          width={`${pct}%`}
          transition="width 0.7s ease-out"
        />
      </Box>
      {roastText && (
        <RoastProse content={roastText} />
      )}
    </Box>
  );
};

// --- Tab button ---
const TabBtn = ({ label, icon, isActive, onClick, count }: { label: string; icon: string; isActive: boolean; onClick: () => void; count?: number }) => (
  <Box
    as="button"
    px={{ base: 3, md: 4 }}
    py={3}
    borderBottom="2px solid"
    borderColor={isActive ? 'orange.500' : 'transparent'}
    color={isActive ? 'white' : 'whiteAlpha.500'}
    fontWeight="medium"
    fontSize={{ base: '13px', md: '14px' }}
    cursor="pointer"
    transition="all 0.15s"
    _hover={{ color: 'white' }}
    onClick={onClick}
    display="flex"
    alignItems="center"
    gap={{ base: '6px', md: '8px' }}
    whiteSpace="nowrap"
    flexShrink={0}
  >
    <Box as="span" style={{ fontSize: '14px' }}>{icon}</Box>
    {label}
    {count !== undefined && (
      <Box as="span" color={isActive ? 'whiteAlpha.700' : 'whiteAlpha.400'}>
        ({count})
      </Box>
    )}
  </Box>
);

// --- Social platform icons (SVG) ---
const SOCIAL_ICONS: Record<string, JSX.Element> = {
  x: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
  twitter: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
  github: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/></svg>,
  linkedin: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
  instagram: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>,
  tiktok: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>,
  facebook: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
  reddit: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/></svg>,
};

// --- Build social URL ---
const buildSocialUrl = (platform: string, handle: string): string | null => {
  if (!handle || handle === 'null' || handle.trim() === '') return null;
  const h = handle.trim();
  if (platform === 'x' || platform === 'twitter') return `https://x.com/${h.replace(/^@/, '')}`;
  if (platform === 'github') return h.startsWith('http') ? h : `https://github.com/${h}`;
  if (platform === 'linkedin') return h.startsWith('http') ? h : `https://linkedin.com/in/${h}`;
  if (platform === 'instagram') return h.startsWith('http') ? h : `https://instagram.com/${h.replace(/^@/, '')}`;
  if (platform === 'tiktok') return h.startsWith('http') ? h : `https://tiktok.com/@${h.replace(/^@/, '')}`;
  if (platform === 'facebook') return h.startsWith('http') ? h : `https://facebook.com/${h}`;
  if (platform === 'reddit') return h.startsWith('http') ? h : `https://reddit.com/${h.startsWith('r/') ? h : `r/${h}`}`;
  if (h.startsWith('http')) return h;
  return null;
};

// --- Share Modal (inspired by InviteModal design) ---
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ROAST_SHARE_EMAIL_SUBJECT = 'Check out this AI Project Roast';

const RoastShareModal = ({ isOpen, onClose, slug, siteName, score }: {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
  siteName: string;
  score: number;
}) => {
  const publicUrl = `${ROAST_PUBLIC_BASE}/${slug}/`;
  const shareText = `${siteName} scored ${score}/100 on BigDevSoon's AI Roast 🔥 Check it out:`;

  return (
    <Modal size="xl" isOpen={isOpen} onClose={onClose} header={null} footer={null}>
      <VStack spacing={5} py={4} px={1}>
        {/* Header with fire icon */}
        <VStack spacing={3} animation={`${fadeIn} 0.5s ease-out`}>
          <Box
            p={3}
            borderRadius="full"
            bg="whiteAlpha.100"
            border="1px solid"
            borderColor="whiteAlpha.200"
          >
            <Box as="span" style={{ fontSize: '28px', lineHeight: 1, display: 'block' }}>🔥</Box>
          </Box>
          <VStack spacing={1}>
            <Text fontSize="xLarge" fontWeight="bold">
              Share this roast
            </Text>
            <HStack spacing={1} flexWrap="wrap" justifyContent="center">
              <Text fontWeight="regular" opacity="secondary" fontSize="small">
                {siteName} scored
              </Text>
              <Text fontSize="small" fontWeight="bold" color="orange.400">
                {score}/100
              </Text>
            </HStack>
          </VStack>
        </VStack>

        {/* Public link card */}
        <Box
          w="100%"
          p={4}
          borderRadius={12}
          bg="whiteAlpha.50"
          border="1px solid"
          borderColor="whiteAlpha.100"
          animation={`${fadeIn} 0.5s ease-out 0.1s both`}
        >
          <VStack spacing={3}>
            <Text fontSize="small" opacity="secondary" fontWeight="semibold">
              Public link
            </Text>
            <LinkInput link={publicUrl} />
          </VStack>
        </Box>

        {/* Share via section */}
        <VStack
          spacing={3}
          w="100%"
          animation={`${fadeIn} 0.5s ease-out 0.2s both`}
        >
          <Text fontSize="small" opacity="secondary" fontWeight="semibold">
            Share via
          </Text>
          <ShareButtons
            text={shareText}
            link={publicUrl}
            emailSubject={ROAST_SHARE_EMAIL_SUBJECT}
          />
        </VStack>
      </VStack>
    </Modal>
  );
};

// --- Creator Boost Promo Card (dismissible, compact square on left) ---
const BOOST_PROMO_DISMISSED_KEY = 'bds_roast_boost_promo_dismissed';

const BoostCreatorCard = () => {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(BOOST_PROMO_DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(BOOST_PROMO_DISMISSED_KEY, '1');
    } catch {
      // Ignore storage errors
    }
  };

  return (
    <Box mb={6} position="relative">
      <Box
        as="a"
        href="https://bigdevsoon.me/boost/"
        target="_blank"
        rel="noopener noreferrer"
        display="block"
        py={3}
        pl={4}
        pr={4}
        borderRadius="xl"
        border="1px solid"
        borderColor="rgba(139,92,246,0.25)"
        bgGradient="linear(to-br, rgba(139,92,246,0.08), rgba(249,115,22,0.06))"
        cursor="pointer"
        transition="all 0.2s"
        _hover={{ borderColor: 'rgba(139,92,246,0.4)', bgGradient: 'linear(to-br, rgba(139,92,246,0.12), rgba(249,115,22,0.08))' }}
        textDecoration="none"
      >
        <HStack spacing={4} alignItems="center">
          <VStack spacing={0.5} alignItems="flex-start">
            <Text fontSize="small" fontWeight="bold">Boost your project to 25,000+ developers</Text>
            <Text fontSize="xMicro" opacity="tertiary">
              Negotiate offers for early adopters at adrian@bigdevsoon.me
            </Text>
          </VStack>
          <Box
            px={3}
            py={1.5}
            borderRadius="lg"
            background="linear-gradient(135deg, rgba(139,92,246,0.25), rgba(249,115,22,0.2))"
            border="1px solid"
            borderColor="rgba(139,92,246,0.3)"
            display="inline-flex"
            alignItems="center"
            flexShrink={0}
          >
            <Box as="span" style={{ fontSize: '12px', fontWeight: 600, color: '#c4b5fd' }}>🚀 Boost →</Box>
          </Box>
        </HStack>
      </Box>
      <Box
        as="button"
        position="absolute"
        top={3}
        right={3}
        onClick={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); handleDismiss(); }}
        cursor="pointer"
        opacity={0.4}
        _hover={{ opacity: 0.8 }}
        transition="opacity 0.15s"
        background="none"
        border="none"
        color="white"
        p={1}
        zIndex={1}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </Box>
    </Box>
  );
};

// --- Rank badges ---
const RANK_BADGES: Record<number, { emoji: string; bg: string; border: string; color: string }> = {
  1: { emoji: '🏆', bg: 'rgba(234,179,8,0.15)', border: 'rgba(234,179,8,0.2)', color: '#facc15' },
  2: { emoji: '🥈', bg: 'rgba(156,163,175,0.15)', border: 'rgba(156,163,175,0.2)', color: '#d1d5db' },
  3: { emoji: '🥉', bg: 'rgba(194,120,3,0.15)', border: 'rgba(194,120,3,0.2)', color: '#f59e0b' },
};

// ===== MAIN COMPONENT =====
const RoastDetail = () => {
  useScrollToTop();
  const { roastId } = useParams<{ roastId: string }>();
  const navigate = useNavigate();
  const { id: userId } = useCurrentUser();
  const [searchParams] = useSearchParams();
  const { data: roast, isPending } = useGetRoastDetailQuery({ roastId });
  const { mutate: createRoast, isPending: isReRoasting } = useCreateRoastMutation();
  const { mutate: deleteRoast, isPending: isDeleting } = useDeleteRoastMutation();
  const { data: upvoters } = useGetRoastUpvotersQuery({ roastId, upvoteCount: roast?.upvote_count ?? 0 });
  const { data: creator } = useGetRoastCreatorQuery({ userId: roast?.user_id });
  const editModal = useDisclosure();
  const deleteModal = useDisclosure();
  const shareModal = useDisclosure();
  const [activeTab, setActiveTab] = useState<'roast' | 'comments' | 'breakdown'>('roast');

  // Handle ?tab=comments from list card navigation
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'comments') setActiveTab('comments');
    else if (tab === 'breakdown') setActiveTab('breakdown');
  }, [searchParams]);

  const roastSections = useMemo(() => parseRoastSections(roast?.roast_text ?? ''), [roast?.roast_text]);

  const categories = useMemo(() => {
    if (!roast?.scores) return [];
    return ROAST_CATEGORIES
      .map((key) => {
        const catScore = roast.scores[key] as CategoryScore | undefined;
        if (!catScore || catScore.score === null || catScore.score === undefined) return null;
        return { key, label: CATEGORY_LABELS[key], icon: CATEGORY_ICONS[key] ?? '📋', ...catScore };
      })
      .filter(Boolean) as { key: string; label: string; icon: string; score: number; roast: string }[];
  }, [roast?.scores]);

  if (isPending) {
    return <PageLoader shift />;
  }

  if (!roast) {
    return (
      <Container maxW="1200px">
        <Center mt="20vh">
          <VStack spacing={4}>
            <Text fontSize="xLarge" opacity="secondary">Roast not found</Text>
            <Button scheme="outline" onClick={() => navigate(ERoute.Roasts)}>Back to Roasts</Button>
          </VStack>
        </Center>
      </Container>
    );
  }

  const isOwner = roast.user_id === userId;
  const isLive = roast.status === 'ready';
  const isProcessing = roast.status === 'processing';
  const isRejected = roast.status === 'rejected';
  const rankParam = Number(searchParams.get('rank'));
  const rank = rankParam >= 1 && rankParam <= 3 ? rankParam : null;
  const displayName = roast.site_name || roast.slug;

  // Re-roast cooldown: 7 days from submitted_at
  const RE_ROAST_COOLDOWN_DAYS = 7;
  const daysSinceSubmitted = roast.submitted_at
    ? Math.floor((Date.now() - new Date(roast.submitted_at).getTime()) / (1000 * 60 * 60 * 24))
    : 0;
  const canReRoast = isOwner && isLive && daysSinceSubmitted >= RE_ROAST_COOLDOWN_DAYS;
  const daysUntilReRoast = Math.max(0, RE_ROAST_COOLDOWN_DAYS - daysSinceSubmitted);

  return (
    <Container maxW="1200px" width="100%" px={{ base: 3, md: 6 }} overflow="hidden">
      <SEO
        title={`BigDevSoon | ${displayName} — Roast`}
        description={roast.summary || `AI roast of ${roast.url}`}
        url={`${process.env.REACT_APP_SITE_URL}roasts/${roast.id}`}
      />

      {/* Actions */}
      <HStack justifyContent="space-between" py={3} flexWrap="wrap" spacing={2}>
        {isLive ? (
          <a
            href={`${ROAST_PUBLIC_BASE}/${roast.slug}/`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Text
              fontSize="small"
              color="purple.300"
              _hover={{ color: 'purple.200' }}
              fontWeight="medium"
            >
              View public page →
            </Text>
          </a>
        ) : (
          <Box />
        )}
        <HStack spacing={2}>
          {isOwner && isLive && (
            <Tooltip
              label={canReRoast ? '3 BigTokens — fresh AI analysis' : `Available in ${daysUntilReRoast} day${daysUntilReRoast !== 1 ? 's' : ''}`}
              hasArrow
              placement="bottom"
            >
              <Box>
                <Button
                  scheme="primary"
                  size="sm"
                  onClick={() => createRoast({ url: roast.url })}
                  isLoading={isReRoasting}
                  isDisabled={!canReRoast}
                >
                  Re-roast
                </Button>
              </Box>
            </Tooltip>
          )}
          {isOwner && (
            <Button scheme="outline" size="sm" onClick={editModal.onOpen}>
              Edit
            </Button>
          )}
          {isOwner && (
            <Button scheme="danger" size="sm" onClick={deleteModal.onOpen}>
              Delete
            </Button>
          )}
        </HStack>
      </HStack>

      {/* Processing banner */}
      {isProcessing && (
        <Box mb={4} p={4} borderRadius="xl" backgroundColor="rgba(249,115,22,0.08)" border="1px solid" borderColor="rgba(249,115,22,0.15)">
          <HStack spacing={3}>
            <Box as="span" color="orange.400" style={{ fontSize: '16px' }}>⏳</Box>
            <VStack spacing={0} alignItems="flex-start">
              <Text fontSize="small" fontWeight="semibold" color="orange.400">
                AI is analyzing this project
              </Text>
              <Text fontSize="xMicro" opacity="tertiary">
                This usually takes under a minute. Refresh to check.
              </Text>
            </VStack>
          </HStack>
        </Box>
      )}

      {/* Rejected banner */}
      {isRejected && (
        <Box mb={4} p={4} borderRadius="xl" backgroundColor="rgba(239,68,68,0.08)" border="1px solid" borderColor="rgba(239,68,68,0.2)">
          <HStack spacing={3} justifyContent="space-between" flexWrap="wrap">
            <HStack spacing={3}>
              <Box as="span" style={{ fontSize: '16px' }}>❌</Box>
              <VStack spacing={0} alignItems="flex-start">
                <Text fontSize="small" fontWeight="semibold" color="red.400">
                  Generation failed
                </Text>
                <Text fontSize="xMicro" opacity="tertiary">
                  AI could not analyze this project. No BigTokens were deducted.
                </Text>
              </VStack>
            </HStack>
            {isOwner && (
              <Button scheme="danger" size="sm" onClick={deleteModal.onOpen}>
                Delete
              </Button>
            )}
          </HStack>
        </Box>
      )}

      {/* Header card */}
      <Box
        p={{ base: 4, md: 6 }}
        borderRadius="xl"
        border="1px solid"
        borderColor="whiteAlpha.100"
        backgroundColor="whiteAlpha.50"
        mb={6}
        position="relative"
      >
        {/* Roasted date — top right (desktop only, mobile shown below tags) */}
        {roast.submitted_at && (
          <Box position="absolute" top={5} right={5} display={{ base: 'none', md: 'block' }}>
            <Text fontSize="xMicro" opacity="tertiary">
              {(() => {
                const isReRoasted = roast.updated_at &&
                  (new Date(roast.updated_at).getTime() - new Date(roast.submitted_at).getTime() > 86400000);
                const d = isReRoasted ? new Date(roast.updated_at!) : new Date(roast.submitted_at);
                return `${isReRoasted ? 'Re-roasted' : 'Roasted'} ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
              })()}
            </Text>
          </Box>
        )}
        <HStack spacing={{ base: 3, md: 5 }} alignItems="flex-start">
          {roast.site_logo_url ? (
            <Box
              width={{ base: '44px', md: '56px' }}
              height={{ base: '44px', md: '56px' }}
              borderRadius="xl"
              border="1px solid"
              borderColor="whiteAlpha.100"
              backgroundColor="whiteAlpha.50"
              overflow="hidden"
              flexShrink={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              p={{ base: '6px', md: '8px' }}
            >
              <Image
                src={roast.site_logo_url}
                alt=""
                width="100%"
                height="100%"
                objectFit="contain"
                borderRadius="md"
              />
            </Box>
          ) : (
            <Box
              width={{ base: '44px', md: '56px' }}
              height={{ base: '44px', md: '56px' }}
              borderRadius="xl"
              bgGradient="linear(to-br, orange.500, red.500)"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <Box as="span" style={{ fontSize: '18px', fontWeight: 700, color: 'white', lineHeight: 1 }}>
                {displayName.charAt(0).toUpperCase()}
              </Box>
            </Box>
          )}
          <VStack alignItems="flex-start" spacing={1.5} flex={1} minWidth={0}>
            <HStack spacing={3} flexWrap="wrap" alignItems="center">
              <Box as="span" fontSize={{ base: '18px', md: '22px' }} fontWeight="bold" color="white">{displayName}</Box>
              {rank && RANK_BADGES[rank] && (
                <Box
                  px={2}
                  py={0.5}
                  borderRadius="full"
                  backgroundColor={RANK_BADGES[rank].bg}
                  border="1px solid"
                  borderColor={RANK_BADGES[rank].border}
                  display="flex"
                  alignItems="center"
                  gap="4px"
                >
                  <Box as="span" style={{ fontSize: '12px', lineHeight: 1 }}>{RANK_BADGES[rank].emoji}</Box>
                  <Box as="span" style={{ fontSize: '12px', fontWeight: 700, color: RANK_BADGES[rank].color }}>
                    #{rank}
                  </Box>
                </Box>
              )}
              {roast.is_promoted && (
                <Box
                  px={2}
                  py={0.5}
                  borderRadius="full"
                  backgroundColor="rgba(139,92,246,0.15)"
                  border="1px solid"
                  borderColor="rgba(139,92,246,0.2)"
                >
                  <Box as="span" style={{ fontSize: '10px', fontWeight: 700, color: '#c4b5fd' }}>
                    PROMOTED
                  </Box>
                </Box>
              )}
            </HStack>
            {roast.site_description && (
              <Text fontSize="small" opacity="secondary" lineHeight="1.5">{roast.site_description}</Text>
            )}
            {/* Tags row */}
            <HStack spacing={2} flexWrap="wrap" mt={1}>
              {roast.category && roast.category !== 'other' && (
                <Box px={2.5} py={1} borderRadius="md" backgroundColor="whiteAlpha.100">
                  <Box as="span" style={{ fontSize: '12px', fontWeight: 500, color: 'rgba(255,255,255,0.6)' }}>
                    {CATEGORY_DISPLAY_LABELS[roast.category] ?? roast.category}
                  </Box>
                </Box>
              )}
              {roast.pricing_model && roast.pricing_model !== 'unknown' && (
                <Box px={2.5} py={1} borderRadius="md" backgroundColor="whiteAlpha.100">
                  <Box as="span" style={{ fontSize: '12px', fontWeight: 500, color: 'rgba(255,255,255,0.6)' }}>
                    {PRICING_DISPLAY_LABELS[roast.pricing_model] ?? roast.pricing_model}
                  </Box>
                </Box>
              )}
              {roast.tags?.slice(0, 3).map((tag) => (
                <Box key={tag} px={2.5} py={1} borderRadius="md" backgroundColor="rgba(249,115,22,0.08)">
                  <Box as="span" style={{ fontSize: '12px', fontWeight: 500, color: 'rgba(249,115,22,0.8)' }}>
                    {tag}
                  </Box>
                </Box>
              ))}
            </HStack>
            {/* Mobile-only date */}
            {roast.submitted_at && (
              <Box display={{ base: 'block', md: 'none' }} mt={1}>
                <Text fontSize="xMicro" opacity="tertiary">
                  {(() => {
                    const isReRoasted = roast.updated_at &&
                      (new Date(roast.updated_at).getTime() - new Date(roast.submitted_at).getTime() > 86400000);
                    const d = isReRoasted ? new Date(roast.updated_at!) : new Date(roast.submitted_at);
                    return `${isReRoasted ? 'Re-roasted' : 'Roasted'} ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
                  })()}
                </Text>
              </Box>
            )}
          </VStack>
        </HStack>

        {/* Mobile-only: compact overall score */}
        {isLive && roast.scores && (
          <HStack
            display={{ base: 'flex', md: 'none' }}
            mt={4}
            p={3}
            borderRadius="lg"
            border="1px solid"
            borderColor={getOverallBorderColor(roast.scores.overall)}
            backgroundColor="whiteAlpha.50"
            spacing={3}
            alignItems="center"
          >
            <Text fontSize="xLarge" fontWeight="bold" color={getOverallScoreColor(roast.scores.overall)} lineHeight={1}>
              {roast.scores.overall}<Box as="span" fontSize="small">%</Box>
            </Text>
            <VStack spacing={0} alignItems="flex-start" flex={1}>
              <Text fontSize="xMicro" opacity="secondary" fontWeight="medium">Overall Score</Text>
              <Text fontSize="xMicro" opacity="tertiary">
                {roast.scores.applicable_categories} categories scored
              </Text>
            </VStack>
          </HStack>
        )}

        {/* CTAs + socials row */}
        <Box mt={4} display="flex" flexDirection={{ base: 'column', sm: 'row' }} justifyContent="space-between" gap={3}>
          <HStack spacing={3} flexWrap="wrap">
            <Box
              as="a"
              href={roast.url}
              target="_blank"
              rel="noopener noreferrer"
              display="inline-flex"
              alignItems="center"
              gap="8px"
              px={{ base: 4, md: 5 }}
              py={{ base: 2, md: 2.5 }}
              borderRadius="lg"
              background="linear-gradient(90deg, #f97316 0%, #ef4444 100%)"
              color="white"
              fontSize={{ base: '13px', md: '14px' }}
              fontWeight="medium"
              cursor="pointer"
              transition="all 0.2s"
              _hover={{ opacity: 0.9 }}
            >
              Visit Website
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
            </Box>
            <RoastVoteButton roastId={roast.id} upvoteCount={roast.upvote_count ?? 0} />
          </HStack>
          <HStack spacing={1.5}>
            {roast.socials && Object.entries(roast.socials)
              .filter(([platform]) => platform !== 'website' && platform !== 'github')
              .map(([platform, handle]) => {
              const socialUrl = buildSocialUrl(platform, handle);
              if (!socialUrl) return null;
              const icon = SOCIAL_ICONS[platform];
              return (
                <a key={platform} href={socialUrl} target="_blank" rel="noopener noreferrer">
                  <Tooltip label={platform} hasArrow placement="top">
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      width="32px"
                      height="32px"
                      borderRadius="lg"
                      backgroundColor="whiteAlpha.50"
                      color="whiteAlpha.400"
                      _hover={{ backgroundColor: 'whiteAlpha.200', color: 'white' }}
                      transition="all 0.15s"
                    >
                      {icon ?? <Box as="span" style={{ fontSize: '11px' }}>{platform}</Box>}
                    </Box>
                  </Tooltip>
                </a>
              );
            })}
            {/* GitHub icon — always right before email */}
            {roast.socials?.github && buildSocialUrl('github', roast.socials.github) && (
              <a href={buildSocialUrl('github', roast.socials.github)!} target="_blank" rel="noopener noreferrer">
                <Tooltip label="github" hasArrow placement="top">
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    width="32px"
                    height="32px"
                    borderRadius="lg"
                    backgroundColor="whiteAlpha.50"
                    color="whiteAlpha.400"
                    _hover={{ backgroundColor: 'whiteAlpha.200', color: 'white' }}
                    transition="all 0.15s"
                  >
                    {SOCIAL_ICONS.github}
                  </Box>
                </Tooltip>
              </a>
            )}
            {roast.contact_email && (
              <a href={`mailto:${roast.contact_email}`}>
                <Tooltip label={roast.contact_email} hasArrow placement="top">
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    width="32px"
                    height="32px"
                    borderRadius="lg"
                    backgroundColor="whiteAlpha.50"
                    color="whiteAlpha.400"
                    _hover={{ backgroundColor: 'whiteAlpha.200', color: 'white' }}
                    transition="all 0.15s"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="4" width="20" height="16" rx="2" />
                      <path d="M22 7l-8.97 5.7a1.94 1.94 0 01-2.06 0L2 7" />
                    </svg>
                  </Box>
                </Tooltip>
              </a>
            )}
            {isOwner && isLive && (
              <Tooltip label="Share this roast" hasArrow placement="top">
                <Box
                  as="button"
                  onClick={shareModal.onOpen}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width="32px"
                  height="32px"
                  borderRadius="lg"
                  backgroundColor="whiteAlpha.50"
                  color="whiteAlpha.400"
                  cursor="pointer"
                  transition="all 0.15s"
                  _hover={{ backgroundColor: 'whiteAlpha.200', color: 'white' }}
                  flexShrink={0}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="18" cy="5" r="3" />
                    <circle cx="6" cy="12" r="3" />
                    <circle cx="18" cy="19" r="3" />
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                  </svg>
                </Box>
              </Tooltip>
            )}
          </HStack>
        </Box>
      </Box>

      {/* Boost promo for creators */}
      {isOwner && isLive && <BoostCreatorCard />}

      {/* Two-column layout: sidebar (scores) + content (tabs) */}
      <Grid
        templateColumns={{ base: '1fr', md: '300px 1fr' }}
        gap={6}
      >
        {/* Left sidebar: screenshot + score card (sticky) — hidden on mobile */}
        <GridItem display={{ base: 'none', md: 'block' }}>
          <VStack
            spacing={4}
            position="sticky"
            top="24px"
            maxHeight="calc(100vh - 48px)"
            overflowY="auto"
            sx={{
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-track': { bg: 'transparent' },
              '&::-webkit-scrollbar-thumb': { bg: 'whiteAlpha.200', borderRadius: 'full' },
            }}
          >
            {/* Screenshot */}
            <Box width="100%" borderRadius="xl" overflow="hidden" border="1px solid" borderColor="whiteAlpha.100" maxHeight="180px" flexShrink={0}>
              {roast.screenshot_url ? (
                <Image src={roast.screenshot_url} alt={displayName} width="100%" height="100%" objectFit="cover" />
              ) : (
                <Center height="200px" backgroundColor="whiteAlpha.50">
                  <Text fontSize="xLarge" opacity="tertiary">
                    {isProcessing ? '⏳' : '📷'}
                  </Text>
                </Center>
              )}
            </Box>

            {/* Published by */}
            {creator && (
              <Box
                as="a"
                href={`/profile/${creator.screen_name}`}
                target="_blank"
                rel="noopener noreferrer"
                width="100%"
                p={4}
                borderRadius="xl"
                border="1px solid"
                borderColor="whiteAlpha.100"
                backgroundColor="whiteAlpha.50"
                cursor="pointer"
                transition="all 0.2s"
                _hover={{ borderColor: 'orange.500', opacity: 0.9 }}
                textDecoration="none"
              >
                <Text fontSize="xMicro" opacity="tertiary" mb={3} textTransform="uppercase" letterSpacing="wider" textAlign="center">
                  Published by
                </Text>
                <VStack spacing={2}>
                  <UserAvatar
                    src={creator.avatar_url ?? undefined}
                    experience={creator.experience}
                    size="big"
                  />
                  <Text fontSize="small" fontWeight="semibold" textAlign="center">
                    {getCreatorDisplayName(creator.name, creator.screen_name)}
                  </Text>
                </VStack>
              </Box>
            )}

            {/* Overall score card */}
            {isLive && roast.scores && (
              <Box
                width="100%"
                p={4}
                borderRadius="xl"
                border="2px solid"
                borderColor={getOverallBorderColor(roast.scores.overall)}
                backgroundColor="whiteAlpha.50"
                boxShadow={getOverallGlowShadow(roast.scores.overall)}
              >
                <VStack spacing={0} mb={3} textAlign="center">
                  <Text fontSize="xxLarge" fontWeight="bold" color={getOverallScoreColor(roast.scores.overall)} lineHeight={1}>
                    {roast.scores.overall}
                    <Box as="span" fontSize="medium">%</Box>
                  </Text>
                  <Text fontSize="xMicro" opacity="tertiary">
                    overall &middot; {roast.scores.applicable_categories} categories
                  </Text>
                </VStack>

                <VStack spacing={2} width="100%">
                  {categories.map((cat) => (
                    <ScoreBar key={cat.key} label={cat.label} icon={cat.icon} score={cat.score} />
                  ))}
                </VStack>
              </Box>
            )}

            {/* Upvoted by */}
            {upvoters.length > 0 && (
              <Box
                width="100%"
                p={3}
                borderRadius="xl"
                border="1px solid"
                borderColor="whiteAlpha.100"
                backgroundColor="whiteAlpha.50"
              >
                <Text fontSize="xMicro" opacity="tertiary" mb={2} textTransform="uppercase" letterSpacing="wider">
                  Upvoted by
                </Text>
                <Box display="flex" flexWrap="wrap" gap="6px">
                  {upvoters.map((u) => (
                    <Tooltip key={u.user_id} label={u.screen_name} placement="top" hasArrow>
                      <Box
                        as="a"
                        href={`/profile/${u.screen_name}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        cursor="pointer"
                        _hover={{ opacity: 0.8 }}
                        transition="opacity 0.15s"
                      >
                        <Avatar
                          name={u.screen_name}
                          src={u.avatar_url ?? undefined}
                          size="sm"
                        />
                      </Box>
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            )}

          </VStack>
        </GridItem>

        {/* Right: Tabbed content */}
        <GridItem>
          {/* Tab bar */}
          <HStack
            spacing={0}
            borderBottom="1px solid"
            borderColor="whiteAlpha.100"
            mb={6}
            overflowX="auto"
            sx={{ '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}
          >
            <TabBtn label="Roast" icon="🔥" isActive={activeTab === 'roast'} onClick={() => setActiveTab('roast')} />
            <TabBtn label="Comments" icon="💬" isActive={activeTab === 'comments'} onClick={() => setActiveTab('comments')} count={roast.comment_count ?? 0} />
            <TabBtn label="Breakdown" icon="📊" isActive={activeTab === 'breakdown'} onClick={() => setActiveTab('breakdown')} />
          </HStack>

          {/* Tab: Roast */}
          {activeTab === 'roast' && (
            <VStack spacing={4} alignItems="flex-start" width="100%">
              {/* Summary / TL;DR */}
              {roast.summary && (
                <Box
                  width="100%"
                  p={{ base: 4, md: 6 }}
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="rgba(249,115,22,0.15)"
                  position="relative"
                  overflow="hidden"
                >
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    bgGradient="linear(to-br, rgba(249,115,22,0.1), rgba(239,68,68,0.05), transparent)"
                  />
                  <VStack spacing={3} alignItems="flex-start" position="relative">
                    <HStack spacing={2}>
                      <Box as="span" style={{ fontSize: '14px' }}>✨</Box>
                      <Text fontSize="xMicro" fontWeight="bold" color="orange.400" textTransform="uppercase" letterSpacing="wider">
                        TL;DR
                      </Text>
                    </HStack>
                    <Text fontSize="small" fontWeight="medium" lineHeight="1.6" opacity="primary">
                      {roast.summary}
                    </Text>
                  </VStack>
                </Box>
              )}

              {/* Roast text sections */}
              {roastSections.map((section, idx) => {
                const style = SECTION_STYLES[section.type];
                return (
                  <Box key={idx} width="100%" borderRadius="xl" border="1px solid" borderColor={style.border} overflow="hidden">
                    {/* Section header */}
                    <Box
                      px={{ base: 4, md: 6 }}
                      py={3.5}
                      bgGradient={style.bgGradient}
                      borderBottom="1px solid"
                      borderColor={style.borderBottom}
                    >
                      <HStack spacing={2.5}>
                        <Center
                          width="32px"
                          height="32px"
                          borderRadius="lg"
                          backgroundColor={style.accentBg}
                        >
                          <Box as="span" style={{ fontSize: '14px' }}>{SECTION_ICONS[section.type]}</Box>
                        </Center>
                        <Text fontSize="medium" fontWeight="bold" color={style.titleColor}>
                          {section.title}
                        </Text>
                      </HStack>
                    </Box>
                    {/* Section content */}
                    <Box px={{ base: 4, md: 6 }} py={5} backgroundColor="whiteAlpha.50">
                      <RoastProse content={section.content} />
                    </Box>
                  </Box>
                );
              })}
            </VStack>
          )}

          {/* Tab: Comments */}
          {activeTab === 'comments' && (
            <RoastCommentSection roastId={roast.id} commentCount={roast.comment_count ?? 0} />
          )}

          {/* Tab: Breakdown */}
          {activeTab === 'breakdown' && (
            <VStack spacing={4} alignItems="flex-start" width="100%">
              {categories
                .sort((a, b) => a.score - b.score)
                .map((cat) => (
                  <ScoreBarFull
                    key={cat.key}
                    label={cat.label}
                    icon={cat.icon}
                    score={cat.score}
                    roastText={cat.roast}
                  />
                ))}
            </VStack>
          )}
        </GridItem>
      </Grid>

      {/* Share modal (creator only) */}
      {isOwner && isLive && (
        <RoastShareModal
          isOpen={shareModal.isOpen}
          onClose={shareModal.onClose}
          slug={roast.slug}
          siteName={displayName}
          score={roast.scores?.overall ?? 0}
        />
      )}

      {/* Edit modal */}
      {isOwner && (
        <RoastMetadataEditor
          roast={roast}
          isOpen={editModal.isOpen}
          onClose={editModal.onClose}
        />
      )}

      {/* Delete confirmation modal */}
      {isOwner && (
        <Modal
          size="md"
          isOpen={deleteModal.isOpen}
          onClose={deleteModal.onClose}
          onSubmit={() => {
            deleteRoast({ roastId: roast.id }, {
              onSuccess: () => {
                deleteModal.onClose();
                navigate(ERoute.Roasts);
              },
            });
          }}
          header="Delete this roast?"
          footer={
            <HStack spacing={2}>
              <Button scheme="ghost" size="sm" onClick={deleteModal.onClose}>
                Cancel
              </Button>
              <Button scheme="danger" size="sm" isLoading={isDeleting} type="submit">
                Delete permanently
              </Button>
            </HStack>
          }
        >
          <VStack spacing={3} alignItems="flex-start">
            <Text opacity="secondary">
              This will permanently delete the roast for <Text as="span" fontWeight="bold">{displayName}.</Text>
            </Text>
            <Text opacity="secondary">
              All votes and comments will be removed. This action cannot be undone.
            </Text>
          </VStack>
        </Modal>
      )}
    </Container>
  );
};

export default RoastDetail;
